// Pure rules for the TRAI compliance screens, kept out of the components so they can be tested.
//
// Every screen here is SHADOW: the server refuses nothing because of this work. The words say so,
// because a red "would suspend" read without that context looks like an outage.

/** Roles that may write declarations and complaints — the server's PLATFORM_PROVIDER_ADMINS list. */
export const COMPLIANCE_ADMINS = [
  'master_admin', 'platform_admin', 'super_admin', 'service_admin', 'provider_owner', 'provider_admin',
];

/** Roles that may set an organisation's own category defaults — the server's ORG_ADMINS list. */
export const CATEGORY_ADMINS = [
  ...COMPLIANCE_ADMINS, 'operator_admin', 'org_admin', 'business_owner', 'business_admin',
];

/** May list across every organisation. */
export const PLATFORM_ROLES = ['master_admin', 'platform_admin'];

export const hasRole = (role: string | null | undefined, allowed: string[]): boolean =>
  !!role && allowed.includes(role.toLowerCase());

/** An OPEN complaint past its evidence deadline. The server says so too; this covers a stale page. */
export const isOverdue = (c: { status: string; evidenceDueAt?: string | null }, now: Date = new Date()): boolean =>
  c.status === 'OPEN' && !!c.evidenceDueAt && new Date(asUtc(c.evidenceDueAt)).getTime() < now.getTime();

/** Server timestamps are UTC LocalDateTime without a zone: read them as UTC, not as local time. */
export const asUtc = (t: string): string => (/[zZ]|[+-]\d{2}:?\d{2}$/.test(t) ? t : `${t}Z`);

export const formatWhen = (t?: string | null): string => {
  if (!t) return '—';
  const d = new Date(asUtc(t));
  return Number.isNaN(d.getTime()) ? t : d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
};

/** Days until (positive) or since (negative) the deadline, rounded towards zero. */
export const daysLeft = (t: string, now: Date = new Date()): number =>
  Math.trunc((new Date(asUtc(t)).getTime() - now.getTime()) / 86_400_000);

export type Tone = 'success' | 'amber' | 'danger' | 'info';

export const levelTone = (level: string): Tone =>
  level === 'WOULD_SUSPEND' ? 'danger' : level === 'WARNING' ? 'amber' : 'success';

export const LEVEL_LABEL: Record<string, string> = {
  OK: 'Below threshold',
  WARNING: 'Close to threshold',
  WOULD_SUSPEND: 'Threshold met — would be suspended',
};

export const SERIES_LABEL: Record<string, string> = {
  PROMOTIONAL_140: '140 (promotional)',
  SERVICE_1600: '1600 (BFSI / Government)',
  SERVICE_1601: '1601 (utilities / logistics)',
  ORDINARY: 'Ordinary number',
};

/** Plain words for the report's and the number list's issue codes. */
export const describeIssue = (code: string): string => {
  const m = /^DECLARED_(\w+?)_USED_FOR_(\w+)$/.exec(code);
  if (m) return `Declared ${m[1].toLowerCase()} but used for ${m[2].toLowerCase()} calls`;
  switch (code) {
    case 'UNDECLARED': return 'Not declared for A2P';
    case 'NOT_OUR_NUMBER': return 'Not a number this platform holds';
    case 'DECLARED_WITHOUT_CATEGORY': return 'Declared without a category';
    case 'PROMOTIONAL_NOT_ON_140': return 'Promotional calls from a non-140 number';
    case 'SERVICE_ON_140': return 'Service calls from a 140 number';
    case 'UNCLASSIFIED_CALLS': return 'Calls with no category';
    default: return code;
  }
};

/** Which consent verdicts mean "the call would have been refused" in shadow. */
export const isWouldRefuse = (verdict: string): boolean => /^(WOULD_REFUSE|REFUSED)/.test(verdict);

/** Reads a chosen file as text (UTF-8); used for every CSV import. */
export const readFileText = (f: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ''));
    r.onerror = () => reject(r.error || new Error('Could not read the file'));
    r.readAsText(f);
  });

/** Datetime-local input value → ISO with the browser's offset, so the server can place it. */
export const localInputToIso = (v: string): string | undefined => {
  if (!v) return undefined;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
};
